<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";

import actions from "~/actions";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { ChildRelationshipIcon } from "~/icons";
import { Placement } from "~/shared/enums";
import type { Option } from "~/shared/types";
import { useDataStore } from "~/stores";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";

const props = defineProps<{
  option: Option;
  isSubitemDropdownMenu?: boolean;
}>();

const dataStore = useDataStore();

const created = ref(false);
const renamed = ref(false);

const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const workingSubitem = ref({
  duid: makeDuid(),
  title: "",
  propertyDuid: props.option.propertyDuid,
  parentDuid: props.option.duid,
  colorHex: makeRandomColorHex(),
});

const property = computed(() => dataStore.getPropertyByDuid(props.option.propertyDuid));

const onRename = (newTitle: string) => {
  if (!workingSubitem.value || !property.value) {
    return;
  }
  workingSubitem.value.title = newTitle;
  renamed.value = true;
};

const dropdownSections = computed(() => {
  if (!props.isSubitemDropdownMenu) {
    return actions.context.option(props.option);
  }
  return actions.context.option(
    workingSubitem.value,
    created.value
      ? undefined
      : {
          property: property.value,
          onRename,
          onChangeColor: (newColorHex) => {
            if (!workingSubitem.value) {
              return;
            }
            workingSubitem.value.colorHex = newColorHex;
          },
          onClose: () => dropdownMenu.value?.close(),
        }
  );
});

watchEffect(() => {
  if (!dropdownMenu.value?.isOpen && renamed.value && !created.value) {
    if (!property.value) {
      return;
    }
    dataStore.createOption(workingSubitem.value.title, property.value, workingSubitem.value);
    created.value = true;
    renamed.value = false;
  }
});
</script>

<template>
  <DropdownMenu
    ref="dropdownMenu"
    :sections="dropdownSections"
    :placement="Placement.RIGHT_TOP"
    :distance="0"
    :max-height-pixels="592"
    :skip-nesting-level="!isSubitemDropdownMenu"
    @click.stop
    @keydown.enter.stop>
    <DropdownMenuItemContent v-if="isSubitemDropdownMenu" :icon="ChildRelationshipIcon" title="Add sub-item" />
    <Tooltip v-else text="Modify option">
      <slot />
    </Tooltip>
  </DropdownMenu>
</template>
